import React, { useState, useRef, useEffect } from 'react';
import { Send, X, ExternalLink, Volume2, VolumeX, Trash2, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useChatbot } from '@/hooks/useChatbot';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};

const ChatbotUI: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { 
    messages, 
    sendMessage, 
    isLoading, 
    leadInfo, 
    clearMessages, 
    isSpeaking, 
    toggleSpeech,
    isListening,
    startVoiceInput,
    stopVoiceInput,
    isVoiceSupported
  } = useChatbot();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Auto-scroll to the bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus on the input field when the chat is opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    try {
      await sendMessage(input);
      setInput('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Sorry, I had trouble processing your message. Please try again.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleVoiceInput = () => {
    if (!isVoiceSupported) {
      toast.error('Voice input is not supported in your browser.');
      return;
    }
    
    if (isListening) {
      stopVoiceInput();
    } else {
      startVoiceInput();
      toast.info('Listening... Speak now.');
    }
  };

  // Calculate whether we have collected lead information
  const hasLeadInfo = !!(leadInfo.name || leadInfo.email || leadInfo.phone);

  return (
    <>
      {/* Fixed chat button - Made larger */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            className="fixed bottom-5 right-5 p-0 w-16 h-16 md:w-24 md:h-24 rounded-full shadow-lg bg-white hover:bg-gray-50 border-2 border-brand-teal z-50 overflow-hidden"
            size="icon"
          >
            <img 
              src="/lovable-uploads/33fde282-8d60-4790-a686-ae234493b947.png" 
              alt="Chat with Max"
              className="w-full h-full object-cover"
            />
          </Button>
        </SheetTrigger>
        
        <SheetContent className="w-full sm:max-w-md md:max-w-lg p-0 flex flex-col h-[90vh] sm:h-[95vh] rounded-t-lg sm:rounded-lg">
          {/* Chat Header */}
          <SheetHeader className="p-4 border-b">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 md:h-16 md:w-16 border-2 border-brand-teal/20">
                <AvatarImage src="/lovable-uploads/33fde282-8d60-4790-a686-ae234493b947.png" alt="Max" />
                <AvatarFallback className="bg-brand-teal text-white">Max</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <SheetTitle className="text-left text-lg md:text-xl">Max</SheetTitle>
                <p className="text-sm text-muted-foreground">ThinkTomorrow.AI Assistant</p>
                {hasLeadInfo && (
                  <div className="mt-1 text-xs text-green-600">
                    {leadInfo.name ? `Thanks, ${leadInfo.name}! ` : ''}
                    {leadInfo.email || leadInfo.phone ? 'Contact info received' : ''}
                  </div>
                )}
              </div>
            </div>
          </SheetHeader>
          
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.role === 'user'
                        ? 'bg-brand-navy text-white rounded-tr-none'
                        : 'bg-white border shadow-sm rounded-tl-none'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1">
                        <Avatar className="h-6 w-6 md:h-8 md:w-8">
                          <AvatarImage src="/lovable-uploads/33fde282-8d60-4790-a686-ae234493b947.png" alt="Max" />
                          <AvatarFallback className="bg-brand-teal text-white text-xs">Max</AvatarFallback>
                        </Avatar>
                        <span className="text-xs md:text-sm font-medium text-gray-700">Max</span>
                      </div>
                    )}
                    <div className="whitespace-pre-wrap text-sm md:text-base">
                      {message.content.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < message.content.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-1 text-xs opacity-70 text-right">
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg p-3 bg-white border shadow-sm rounded-tl-none">
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar className="h-6 w-6 md:h-8 md:w-8">
                        <AvatarImage src="/lovable-uploads/33fde282-8d60-4790-a686-ae234493b947.png" alt="Max" />
                        <AvatarFallback className="bg-brand-teal text-white text-xs">Max</AvatarFallback>
                      </Avatar>
                      <span className="text-xs md:text-sm font-medium text-gray-700">Max</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="h-2 w-2 rounded-full bg-gray-300 animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="h-2 w-2 rounded-full bg-gray-300 animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="h-2 w-2 rounded-full bg-gray-300 animate-bounce"></div>
                      </div>
                      <span className="text-sm text-gray-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              {isListening && (
                <div className="flex justify-end">
                  <div className="bg-green-100 text-green-800 text-sm py-1 px-3 rounded-full">
                    <div className="flex items-center gap-1">
                      <Mic className="h-3 w-3 animate-pulse" />
                      <span>Listening...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          
          {/* Input Area */}
          <div className="p-4 border-t bg-white">
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                className="min-h-[60px] max-h-[120px] resize-none text-base"
                disabled={isLoading || isListening}
              />
              <Button 
                className="bg-brand-teal hover:bg-brand-teal/90 h-[60px] w-[60px] md:h-[60px] md:w-[60px]" 
                onClick={handleSendMessage}
                disabled={isLoading || isListening || !input.trim()}
              >
                <Send className="h-5 w-5 md:h-5 md:w-5" />
              </Button>
            </div>
            <div className="mt-2 text-xs md:text-sm text-center text-muted-foreground flex items-center justify-center gap-2">
              <Link to="/contact" className="text-brand-teal hover:underline inline-flex items-center" onClick={() => setIsOpen(false)}>
                Contact us <ExternalLink className="ml-1 h-3 w-3" />
              </Link>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ChatbotUI;
